<template>
    <div
        id="hamburger-menu"
        class="fixed inset-0 z-40"
        role="menu"
        aria-labelledby="menubutton"
    >
        <div
            class="bg-black-opacity-40 absolute inset-0 transition-opacity duration-500"
            :class="{
                'opacity-0': !active,
                'opacity-100': active,
            }"
            @click="closeMenu"
        />

        <div
            :data-theme="settings.primaryNav_ColorTheme"
            :data-mode="colorPalette(settings?.menu_ColorPalette)"
            class="surface-primary absolute inset-y-0 w-full overflow-auto transition-transform duration-500 max-lg:flex max-lg:max-w-[500px] max-lg:flex-col lg:min-w-[780px] lg:overflow-hidden"
            :class="{
                'left-0': position === 'left',
                '-translate-x-full': !active && position === 'left',
                'translate-x-0': active && position === 'left',
                'left-full': position === 'right',
                'translate-x-0': !active && position === 'right',
                '-translate-x-full!': active && position === 'right',
                'lg:max-w-full': menuWidth === '100',
                'lg:max-w-[75%]': menuWidth === '75',
                'lg:max-w-[50%]': menuWidth === '50',
            }"
        >
            <!-- Top bar-->
            <div
                class="z-30 flex w-full shrink-0 items-center justify-end space-x-2 px-6 py-6 lg:absolute lg:top-0 lg:left-0 lg:px-11"
            >
                <!-- CTA Buttons -->
                <div
                    ref="actionButtons"
                    :data-mode="bgImgPalette"
                    class="mr-auto hidden items-center space-x-2 lg:flex"
                >
                    <template v-if="useActionDropdown">
                        <DDropdown>
                            <template #trigger="dropdownProps">
                                <DButton
                                    theme="transparent"
                                    right-icon="ph:caret-down"
                                    size="lg"
                                    :active="dropdownProps.active"
                                >
                                    Actions
                                </DButton>
                            </template>

                            <NuxtLink
                                v-for="(button, buttonIndex) in buttons"
                                :key="buttonIndex"
                                :to="button.url"
                                :target="button.target"
                                no-prefetch
                            >
                                <DDropdownItem tag="div">
                                    {{ button.text }}
                                </DDropdownItem>
                            </NuxtLink>
                        </DDropdown>
                    </template>

                    <template v-else>
                        <NuxtLink
                            v-for="(button, buttonIndex) in buttons"
                            :key="buttonIndex"
                            :to="button.url"
                            :target="button.target"
                            no-prefetch
                        >
                            <DButton
                                :theme="buttonTheme(button.buttonStyle)"
                                size="lg"
                                tag="div"
                            >
                                {{ button.text }}
                            </DButton>
                        </NuxtLink>
                    </template>
                </div>

                <div
                    :data-mode="submenuColorPalette"
                    class="flex items-center"
                >
                    <!-- search & quicklinks -->
                    <NuxtLink
                        v-if="showSearch"
                        to="/search"
                        :class="{
                            'mr-auto! ml-0! lg:mr-2!': position === 'right',
                            'mr-0! ml-auto! lg:ml-2!': position === 'left',
                        }"
                        no-prefetch
                    >
                        <DButton
                            theme="transparent"
                            icon="ph:magnifying-glass"
                            size="lg"
                            @click="closeMenu"
                        />
                    </NuxtLink>

                    <span class="hidden h-[22px] w-px bg-(--border-neutral-base) lg:block" />

                    <div
                        v-if="
                            quicklinks?.mainNavQuicklinksItem_Links && quicklinks.mainNavQuicklinksItem_Links.length > 0
                        "
                        class="hidden lg:inline-flex"
                    >
                        <DDropdown>
                            <template #trigger="dropdownProps">
                                <DButton
                                    theme="transparent"
                                    right-icon="ph:caret-down"
                                    size="lg"
                                    :active="dropdownProps.active"
                                >
                                    {{ quicklinks.mainNavQuicklinksItem_Heading }}
                                </DButton>
                            </template>

                            <NuxtLink
                                v-for="(quicklink, index) in quicklinks.mainNavQuicklinksItem_Links"
                                :key="index"
                                :to="quicklink.url"
                                :target="quicklink.target"
                                no-prefetch
                            >
                                <DDropdownItem
                                    tag="div"
                                    :leading-icon="iconName(quicklink?.icon)"
                                >
                                    {{ quicklink.text }}
                                </DDropdownItem>
                            </NuxtLink>
                        </DDropdown>
                    </div>

                    <DButton
                        class="menu-button"
                        :theme="settings?.primaryNav_MenuButtonStyle === 'outline' ? 'outline' : 'primary'"
                        icon="ph:x"
                        size="lg"
                        @click="closeMenu"
                    />
                </div>
            </div>

            <!-- Menu Wrapper -->
            <div
                class="h-full w-full overflow-hidden lg:grid"
                :class="{
                    'lg:grid-cols-[0.5fr_1fr_1fr_1fr_0.5fr]': sectionCount === '1',
                    'lg:grid-cols-[0fr_1fr_1fr_0fr]': sectionCount === '2',
                    'lg:grid-cols-[1fr_1fr_1fr_0fr]': sectionCount === '3',
                }"
                @mouseleave="hoveredChildEntry = null"
            >
                <!--Image-->
                <div
                    v-if="showImage"
                    aria-hidden="true"
                    data-mode="brand-invert"
                    class="relative -order-1 row-start-1 hidden min-h-svh bg-cover bg-no-repeat lg:block"
                    :class="{
                        'col-start-2': sectionCount !== '3',
                        'col-start-1': sectionCount === '3',
                    }"
                    :style="{
                        backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none',
                        backgroundPosition: `${backgroundImageFocalPoint[0] * 100}% ${backgroundImageFocalPoint[1] * 100}%`,
                    }"
                >
                    <!-- overlay -->
                    <div
                        v-if="settings?.menu_Overlay !== 'off'"
                        class="absolute inset-0 opacity-50"
                        :style="overlayStyle"
                    />
                </div>

                <!-- Left Menu -->
                <div
                    class="border-white-opacity-10 divide-white-opacity-10 relative z-20 col-start-2 row-start-1 mx-auto flex h-full w-full flex-col divide-y overflow-y-auto border-t border-b lg:space-y-1 lg:divide-none lg:overflow-hidden lg:border-transparent lg:pt-40"
                    :class="{
                        'lg:px-6': sectionCount !== '1',
                        'items-center lg:max-w-[420px]': sectionCount === '1',
                        'mx-auto lg:px-36': menuWidth === '100' && sectionCount === '2',
                    }"
                >
                    <div
                        :data-mode="bgImgPalette"
                        class="lg:overflow-y-auto"
                    >
                        <div
                            v-for="(entry, entryIndex) in entries"
                            :key="entryIndex"
                            class="text-neutral-extra-strong rounded-card-sm hover:bg-black-opacity-10 px-6 py-5 transition-all duration-150 lg:px-4 lg:py-3 lg:hover:opacity-100!"
                            :class="{
                                'lg:opacity-50': activeEntry && activeEntry !== entry,
                                'bg-black-opacity-10': activeEntry === entry,
                                'leading-3xl text-3xl': menuWidth === '50',
                                'leading-4xl text-4xl': ['100', '75'].includes(menuWidth),
                            }"
                            @mouseenter="onMouseEnter($event, entry)"
                            @click="handleEntryClick(entry, $event)"
                        >
                            <div class="flex items-center">
                                <NuxtLink
                                    class="hover:text-neutral-extra-strong button-button pr-2 hover:underline"
                                    :class="{
                                        'font-brand-heading': settings?.primaryNav_ApplyHeadingFontMenuItems === true,
                                    }"
                                    :to="entry.redirectLink?.[0].linkUrl ?? `/${entry.uri}`"
                                    :target="entry.redirectLink?.[0].target ?? ''"
                                    @click.stop
                                >
                                    {{ entry.title }}
                                </NuxtLink>

                                <DButton
                                    v-if="entry.children.length > 0"
                                    theme="transparent"
                                    icon="ph:caret-right"
                                    class="ml-auto rotate-90 transition-transform duration-300 lg:hidden lg:rotate-0!"
                                    :class="{
                                        '-rotate-90!': activeEntry === entry,
                                    }"
                                />
                            </div>

                            <!-- mobile only menu -->
                            <div
                                v-if="entry?.children.length > 0"
                                class="-mx-4 grid grid-rows-[0fr] overflow-hidden transition-all duration-300 lg:hidden"
                                :class="{ 'grid-rows-[1fr] pt-4': activeEntry === entry }"
                            >
                                <div class="grid min-h-0 gap-2">
                                    <div
                                        v-for="(childEntry, childEntryIndex) in entry?.children"
                                        :key="childEntryIndex"
                                    >
                                        <div
                                            class="text-neutral-subtle flex cursor-pointer items-center px-4 py-2 text-base font-semibold transition-all duration-150"
                                            @click.stop="toggleChildEntry(childEntry)"
                                        >
                                            <NuxtLink
                                                :to="childEntry.redirectLink?.[0].linkUrl ?? `/${childEntry.uri}`"
                                                :target="childEntry.redirectLink?.[0].target ?? ''"
                                                class="hover:text-neutral-extra-strong pr-2 hover:underline"
                                            >
                                                {{ childEntry.title }}
                                            </NuxtLink>

                                            <DButton
                                                v-if="childEntry.children?.length > 0"
                                                theme="transparent"
                                                icon="ph:caret-down"
                                                class="ml-auto transition-transform duration-300"
                                                :class="{
                                                    'rotate-180': activeChildEntry === childEntry,
                                                }"
                                            />
                                        </div>

                                        <div
                                            class="grid grid-rows-[0fr] overflow-hidden transition-all duration-300"
                                            :class="{
                                                'grid-rows-[1fr]':
                                                    activeChildEntry === childEntry && childEntry.children.length > 0,
                                            }"
                                        >
                                            <div class="min-h-[0px]">
                                                <NuxtLink
                                                    v-for="(subChildEntry, subChildIndex) in childEntry.children"
                                                    :key="subChildIndex"
                                                    :to="
                                                        subChildEntry.redirectLink?.[0].linkUrl ??
                                                        `/${subChildEntry.uri}`
                                                    "
                                                    :target="subChildEntry.redirectLink?.[0].target ?? ''"
                                                    class="text-neutral-base ml-3 flex cursor-pointer items-center px-4 py-2 text-sm transition-all duration-150 hover:underline"
                                                >
                                                    {{ subChildEntry.title }}
                                                </NuxtLink>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- primary nav & menu buttons -->
                    <div
                        v-if="
                            (primaryNavButtons && primaryNavButtons.length > 0) ||
                            (menuButtons && menuButtons.length > 0)
                        "
                        class="flex w-full flex-col space-y-4 p-6 lg:hidden"
                    >
                        <NuxtLink
                            v-for="(button, buttonIndex) in primaryNavButtons"
                            :key="button.id ?? button.url ?? button.text ?? `primary-${buttonIndex}`"
                            :to="button.url"
                        >
                            <DButton
                                :theme="buttonTheme(button.buttonStyle)"
                                :class="{
                                    'justify-start': buttonTheme(button.buttonStyle) === 'text-link',
                                }"
                                size="lg"
                                link-size="lg"
                                wide
                            >
                                {{ button.text }}
                            </DButton>
                        </NuxtLink>
                        <NuxtLink
                            v-for="(button, buttonIndex) in menuButtons"
                            :key="button.id ?? button.url ?? button.text ?? `menu-${buttonIndex}`"
                            :to="button.url"
                        >
                            <DButton
                                :theme="buttonTheme(button.buttonStyle)"
                                :class="{
                                    'justify-start': buttonTheme(button.buttonStyle) === 'text-link',
                                }"
                                size="lg"
                                link-size="lg"
                                wide
                            >
                                {{ button.text }}
                            </DButton>
                        </NuxtLink>
                    </div>

                    <!-- secondary nav buttons -->
                    <div
                        v-if="secondaryNavButtons && secondaryNavButtons.length > 0"
                        class="flex w-full flex-col space-y-1 p-6 lg:hidden"
                    >
                        <NuxtLink
                            v-for="(button, buttonIndex) in secondaryNavButtons"
                            :key="buttonIndex"
                            :to="button.url"
                        >
                            <DButton
                                theme="transparent"
                                :left-icon="iconName(button?.icon)"
                            >
                                {{ button.text }}
                            </DButton>
                        </NuxtLink>
                    </div>

                    <!-- mobile quicklinks -->
                    <div
                        v-if="
                            quicklinks?.mainNavQuicklinksItem_Links && quicklinks.mainNavQuicklinksItem_Links.length > 0
                        "
                        class="flex w-full flex-col space-y-1 p-6 lg:hidden"
                    >
                        <NuxtLink
                            v-for="(quicklink, index) in quicklinks.mainNavQuicklinksItem_Links"
                            :key="index"
                            :to="quicklink.url"
                            :target="quicklink.target"
                            class="text-neutral-strong hover:bg-black-opacity-10 flex cursor-pointer items-center space-x-3 rounded-md p-1 text-sm font-medium transition-all duration-150 lg:p-2"
                        >
                            <div
                                v-if="quicklink?.icon?.name"
                                class="rounded-button-md surface-brand-subtle flex h-9 w-9 items-center justify-center lg:h-11 lg:w-11"
                            >
                                <DIcon :icon="iconName(quicklink.icon)" />
                            </div>
                            <span>{{ quicklink.text }}</span>
                        </NuxtLink>
                    </div>

                    <div class="flex w-full gap-2 p-6 lg:hidden">
                        <NuxtLink
                            v-for="(social, socialIndex) in socialLinks"
                            :key="socialIndex"
                            :to="social.url"
                            :target="social.target"
                            :aria-label="social.title || social.text || social.ariaLabel"
                        >
                            <DButton
                                :icon="iconName(social?.icon)"
                                class="social-media-button"
                                theme="transparent"
                            />
                        </NuxtLink>
                    </div>
                </div>

                <!-- Right Menu -->
                <div
                    :data-mode="submenuColorPalette"
                    class="surface-primary isolate z-10 col-start-3 flex lg:overflow-hidden lg:pt-40"
                    @mouseleave="activeChildEntry = null"
                >
                    <div class="flex w-full items-start p-6 lg:max-w-[420px] lg:overflow-y-auto lg:p-0 lg:px-10">
                        <div class="hidden flex-1 gap-2 lg:grid">
                            <div
                                v-for="(childEntry, childEntryIndex) in activeEntry?.children"
                                :key="childEntryIndex"
                                class="hover:bg-black-opacity-10 rounded-md hover:opacity-100!"
                                :class="{
                                    'bg-black-opacity-10': activeChildEntry === childEntry,
                                    'opacity-50': activeChildEntry && activeChildEntry !== childEntry,
                                }"
                                @mouseenter="
                                    sectionCount === '1' && childEntry.children?.length > 0
                                        ? (hoveredChildEntry = childEntry)
                                        : null
                                "
                            >
                                <div
                                    class="text-neutral-base flex cursor-pointer items-center px-4 py-2 text-lg transition-all duration-150"
                                    @click="toggleChildEntry(childEntry)"
                                >
                                    <NuxtLink
                                        :to="childEntry.redirectLink?.[0].linkUrl ?? `/${childEntry.uri}`"
                                        :target="childEntry.redirectLink?.[0].target ?? ''"
                                        class="hover:text-neutral-extra-strong pr-2 hover:underline"
                                    >
                                        {{ childEntry.title }}
                                    </NuxtLink>

                                    <DButton
                                        v-if="childEntry.children?.length > 0 && sectionCount !== '1'"
                                        theme="transparent"
                                        icon="ph:caret-down"
                                        class="pointer-events-none ml-auto transition-transform duration-300"
                                        :class="{
                                            'rotate-180': activeChildEntry === childEntry,
                                        }"
                                    />
                                </div>

                                <!-- Always show dropdown on click (original behavior) -->
                                <div
                                    v-if="childEntry.children?.length > 0"
                                    class="grid grid-rows-[0fr] overflow-hidden transition-all duration-300"
                                    :class="{
                                        'grid-rows-[1fr]':
                                            activeChildEntry === childEntry && childEntry.children.length > 0,
                                    }"
                                >
                                    <div class="min-h-[0px]">
                                        <NuxtLink
                                            v-for="(subChildEntry, subChildIndex) in childEntry.children"
                                            :key="subChildIndex"
                                            :to="subChildEntry.redirectLink?.[0].linkUrl ?? `/${subChildEntry.uri}`"
                                            :target="subChildEntry.redirectLink?.[0].target ?? ''"
                                            class="text-neutral-base hover:text-neutral-extra-strong flex cursor-pointer items-center px-4 py-2 text-sm transition-all duration-150 hover:underline"
                                            exact-active-class="text-neutral-extra-strong font-medium"
                                        >
                                            {{ subChildEntry.title }}
                                        </NuxtLink>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <BlocksBackgroundElement
                        v-if="backgroundElement"
                        :entry="backgroundElement"
                        class="-z-10 hidden lg:flex"
                    />
                    <div
                        v-else-if="showWatermark"
                        class="pointer-events-none absolute right-0 bottom-0 -z-20 hidden translate-x-1/4 translate-y-1/4 lg:block"
                    >
                        <img
                            :src="watermark.url"
                            alt=""
                            aria-hidden="true"
                        >
                    </div>
                </div>

                <!-- 3rd Level Menu - Displays children of hovered 2nd level item -->
                <div
                    v-if="sectionCount === '1'"
                    data-mode="color-invert"
                    class="isolate col-start-4 flex lg:overflow-hidden lg:pt-40"
                    @mouseleave="hoveredChildEntry = null"
                >
                    <div class="flex w-full items-start p-6 lg:max-w-[420px] lg:overflow-y-auto lg:p-0 lg:px-10">
                        <div class="hidden flex-1 lg:block">
                            <div v-if="hoveredChildEntry">
                                <div
                                    v-for="(subChildEntry, subChildIndex) in hoveredChildEntry.children"
                                    :key="subChildIndex"
                                    class="hover:bg-black-opacity-10 rounded-md"
                                >
                                    <div
                                        class="text-neutral-base flex cursor-pointer items-center px-4 py-3 text-lg transition-all duration-150"
                                    >
                                        <NuxtLink
                                            :to="subChildEntry.redirectLink?.[0].linkUrl ?? `/${subChildEntry.uri}`"
                                            :target="subChildEntry.redirectLink?.[0].target ?? ''"
                                            class="hover:text-neutral-extra-strong pr-2 hover:underline"
                                        >
                                            {{ subChildEntry.title }}
                                        </NuxtLink>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <BlocksBackgroundElement
                        v-if="backgroundElement"
                        :entry="backgroundElement"
                        class="-z-10 hidden lg:flex"
                    />
                    <div
                        v-else-if="showWatermark"
                        class="pointer-events-none absolute right-0 bottom-0 -z-10 hidden translate-x-1/4 translate-y-1/4 lg:block"
                    >
                        <img
                            :src="watermark.url"
                            alt=""
                            aria-hidden="true"
                        >
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { DButton, DDropdown, DDropdownItem, DIcon } from '@digistorm/spark'
    import { useScrollLock } from '@vueuse/core'

    const { iconName } = useIcons()

    const emit = defineEmits(['close'])

    const props = defineProps({
        settings: Object,
        entries: Array,
        position: {
            type: String,
            default: 'right',
        },
        showSearch: Boolean,
        sectionCount: {
            type: String,
            default: '2',
        },
    })

    const { getGlobalSetField } = storeToRefs(useGlobalStore())
    const { buttonTheme } = useButtonTheme()
    const { colorPalette } = useColorPalette()

    const { websiteTier } = storeToRefs(useGlobalStore())

    const route = useRoute()

    const scrollLocked = useScrollLock(window)

    const active = ref(false)
    const actionButtons = ref(null)
    const useActionDropdown = ref(false)

    const activeEntry = ref(null)
    const activeChildEntry = ref(null)
    const hoveredChildEntry = ref(null)

    const DESKTOP_BREAKPOINT = 1024

    const rootUri = computed(() => {
        return first(route.params.uri)
    })

    const childUri = computed(() => {
        return nth(route.params.uri, 1)
    })

    const primaryNavButtons = computed(() => {
        return props.settings.primaryNav_Buttons
            ? props.settings.primaryNav_Buttons.filter((button) => !button.isEmpty)
            : []
    })

    const menuButtons = computed(() => {
        return props.settings.menu_Buttons ? props.settings.menu_Buttons.filter((button) => !button.isEmpty) : []
    })

    const buttons = computed(() => {
        if (menuButtons.value.length > 0) {
            return menuButtons.value
        }
        return primaryNavButtons.value
    })

    const quicklinks = computed(() => {
        return first(props.settings.primaryNav_Quicklinks)
    })

    const menuWidth = computed(() => {
        return props.settings.mainNav_Width || '50'
    })

    const watermark = computed(() => {
        return first(props.settings.primaryNav_Watermark)
    })

    const showWatermark = computed(() => {
        return websiteTier.value === 'foundation' && !!watermark.value
    })

    const backgroundElement = computed(() => {
        return first(props.settings.menu_BackgroundElement)
    })

    const secondaryNavButtons = computed(() => {
        return getGlobalSetField.value('ds_Navigation_GlobalSet', 'secondaryNav_Buttons')
    })

    const socialLinks = computed(() => {
        return getGlobalSetField.value('ds_Socials_GlobalSet', 'socials_SocialLinks')
    })

    const isDesktop = ref(false)

    const checkIfDesktop = throttle(() => {
        if (typeof window !== 'undefined') {
            isDesktop.value = window.innerWidth >= DESKTOP_BREAKPOINT
        }
    }, 800)

    const onMouseEnter = (event, entry) => {
        if (window.innerWidth >= DESKTOP_BREAKPOINT) {
            activeEntry.value = entry
            hoveredChildEntry.value = null
        }
    }

    const handleEntryClick = (entry) => {
        if (entry.children.length > 0) {
            toggleEntry(entry)
        }
    }

    const toggleChildEntry = (entry) => {
        if (activeChildEntry.value === entry) {
            activeChildEntry.value = null
        } else {
            activeChildEntry.value = entry
        }
    }

    const toggleEntry = (entry) => {
        if (activeEntry.value === entry) {
            activeEntry.value = null
            activeChildEntry.value = null
        } else {
            activeEntry.value = entry
            activeChildEntry.value = null
        }
    }

    const closeMenu = () => {
        activeChildEntry.value = null
        hoveredChildEntry.value = null
        active.value = false
        scrollLocked.value = false

        delay(() => {
            emit('close')
        }, 500)
    }

    const showImage = computed(() => {
        return (
            websiteTier.value !== 'foundation' &&
            backgroundImage?.value?.url &&
            props.sectionCount !== '1' &&
            menuWidth.value !== '50'
        )
    })

    const bgImgPalette = computed(() => {
        if (isDesktop.value && showImage.value) {
            return 'brand-invert'
        }
        return ''
    })

    const backgroundImage = computed(() => {
        return first(props.settings?.menu_BackgroundImage)
    })

    const backgroundImageUrl = computed(() => {
        return get(backgroundImage?.value, 'url', '')
    })

    const backgroundImageFocalPoint = computed(() => {
        return get(backgroundImage?.value, 'focalPoint', [0.5, 0.5])
    })

    const overlayStyle = computed(() => {
        return {
            backgroundColor: props.settings?.menu_Overlay === 'custom' ? props.settings?.menu_CustomOverlay : '#000000',
            opacity: props.settings?.menu_OverlayOpacity / 100,
        }
    })

    const submenuColorPalette = computed(() => {
        if (isDesktop.value && !showImage.value) {
            return colorPalette(props.settings?.menu_SubmenuColorPalette)
        }
        return ''
    })

    onMounted(() => {
        scrollLocked.value = true

        if (window.innerWidth >= DESKTOP_BREAKPOINT) {
            activeEntry.value = find(props.entries, { uri: rootUri.value }) || props.entries[0]
            activeChildEntry.value = find(activeEntry.value?.children, { uri: `${rootUri.value}/${childUri.value}` })
        }

        delay(() => {
            active.value = true

            // check if action buttons are overflowing
            if (actionButtons.value) {
                useActionDropdown.value = actionButtons.value.clientWidth > 360
            }
        }, 1)

        checkIfDesktop()

        window.addEventListener('resize', checkIfDesktop)
    })

    onUnmounted(() => {
        window.removeEventListener('resize', checkIfDesktop)
    })
</script>
